import { useApplicationContext } from "app/core/contexts/ApplicationContext"
import { trpc } from "app/core/trpcClient"
import { CloneOptionType } from "app/paywalls/validations"
import moment from "moment"
import { queryTypes, useQueryState } from "next-usequerystate"
import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"
import { Button } from "@superwall/ui/components/button"
import { Dialog, DialogContent, DialogTrigger } from "@superwall/ui/components/dialog"
import { sonner } from "@superwall/ui/components/sonner"
import PaywallPreview from "./PaywallPreview"
import getEditorRouteByPaywall from "app/paywalls/helpers/getEditorRouteByPaywall"
import usePaywallVariables from "app/paywalls/hooks/usePaywallVariables"
import useLocalizedSubstitutions from "app/paywalls/hooks/useLocalizedSubstitutions"
import useIsAdmin from "app/core/hooks/useIsAdmin"
import { SIcon } from "@superwall/legacy-ui"

export interface TemplateModalProps {
  /** The template/paywall to display */
  template: {
    id: number
    name: string
    updatedAt: Date
    templateId?: string
  }
  /** Whether the modal should open initially */
  initiallyOpen?: boolean
  /** Whether this is an unlisted template */
  isUnlisted?: boolean
  /** Whether this is being used in a public context (non-authenticated) */
  isPublic?: boolean
  /** Custom trigger element - if not provided, no trigger is rendered */
  children?: React.ReactNode
  /** Callback when modal opens */
  onOpen?: () => void
  /** Callback when modal closes */
  onClose?: () => void
  /** Whether to sync with URL query params (templateId) */
  syncWithUrl?: boolean
}

interface PaywallPreviewContainerProps {
  paywall: any
  isPublic?: boolean
}

const PaywallPreviewContainer = ({ paywall, isPublic = false }: PaywallPreviewContainerProps) => {
  const variables = usePaywallVariables({
    applicationId: paywall?.applicationId,
    productPreviewPayload: {
      primary: {
        identifier: paywall.primaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
      secondary: {
        identifier: paywall.secondaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
      tertiary: {
        identifier: paywall.tertiaryProductIdentifier,
        locale: { language: "en", region: "US", currency: "USD" },
      },
    },
    useExamplePreviewData: isPublic,
  })

  const productsWithTrialCount = [variables.primary, variables.secondary, variables.tertiary]
    .map((el) => el["trialPeriodDays"])
    .filter((el) => el !== undefined && el != "0").length

  const localizedSubs = useLocalizedSubstitutions({
    applicationId: paywall?.applicationId,
    rawSubs: paywall?.substitutions,
    isPublic,
  })

  return (
    <PaywallPreview
      previewUrl={paywall.url}
      subs={localizedSubs}
      viewFreeTrial={productsWithTrialCount > 0}
      maxWidth={300}
      deviceColor={"hsl(218,14%,30%)"}
      showNotch={false}
      backgroundColor={paywall.backgroundColorHex}
      pageStyles={paywall.pageStyles}
      variables={variables}
      paywall={paywall}
    />
  )
}

export const TemplateModal = ({
  template,
  initiallyOpen = false,
  isUnlisted = false,
  isPublic = false,
  children,
  onOpen,
  onClose,
  syncWithUrl = false,
}: TemplateModalProps) => {
  const router = useRouter()
  const { applicationId } = useApplicationContext()
  const [templateId, setTemplateId] = useQueryState("templateId", queryTypes.integer)
  const [isOpen, setIsOpen] = useState(initiallyOpen)
  const isAdmin = useIsAdmin()

  const duplicatePaywallMutation = trpc.blitzMigration.paywalls.duplicatePaywall.useMutation()

  // Fetch paywall data for both public and private views
  const { data: paywallPublic } = trpc.blitzMigration.paywalls.getPaywallPublic.useQuery(
    { id: template.id },
    {
      suspense: false,
      enabled: isPublic && (isOpen || initiallyOpen || templateId === template.id) && !isUnlisted,
    }
  )

  const { data: paywallPrivate } = trpc.blitzMigration.paywalls.getPaywall.useQuery(
    { id: template.id },
    {
      suspense: false,
      enabled: !isPublic && (isOpen || initiallyOpen || templateId === template.id) && !isUnlisted,
    }
  )

  const paywall = isPublic ? paywallPublic : paywallPrivate

  const handleOpen = useCallback(() => {
    if (isUnlisted && isPublic) {
      return
    }
    setIsOpen(true)
    if (syncWithUrl && isPublic) {
      setTemplateId(template.id, { scroll: false })
    }
    onOpen?.()
  }, [setTemplateId, template.id, isUnlisted, isPublic, syncWithUrl, onOpen])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (!open) {
        if (syncWithUrl && isPublic) {
          setTemplateId(null, { scroll: false })
        }
        onClose?.()
      }
    },
    [setTemplateId, isPublic, syncWithUrl, onClose]
  )

  const handleUseTemplate = useCallback(async () => {
    if (isPublic) {
      if (isUnlisted && "templateId" in template) {
        return router.push(`/templates/${template.templateId}/duplicate`)
      }
      router.push(
        `/select-application?pathname=${encodeURIComponent(
          `applications/:app/templates?templateId=${template.id}`
        )}`
      )
    } else {
      try {
        const newPaywall = await duplicatePaywallMutation.mutateAsync({
          id: template.id,
          name: `${template.name} (Template)`,
          targetApplicationId: applicationId,
          cloneOptionType: CloneOptionType.AsTemplate,
        })

        const newPaywallRoute = getEditorRouteByPaywall({
          paywall: newPaywall,
        })!

        if (newPaywall.v4) {
          const href = newPaywallRoute.href
          if (href && typeof window !== "undefined") {
            window.location.href = href
          }
        } else {
          router.push(newPaywallRoute)
        }
      } catch (error) {
        console.error("Failed to duplicate paywall:", error)
      }
    }
  }, [isPublic, isUnlisted, template, router, duplicatePaywallMutation, applicationId])

  const handleEditTemplate = useCallback(() => {
    if (!paywall) return

    const editorRoute = getEditorRouteByPaywall({
      paywall: paywall,
    })

    if (editorRoute) {
      if ("href" in editorRoute && editorRoute.href) {
        window.location.href = editorRoute.href
      } else {
        router.push(editorRoute)
      }
    }
  }, [paywall, router])

  const handleShareTemplate = useCallback(() => {
    const url = `https://superwall.com/templates?templateId=${template.id}`
    navigator.clipboard.writeText(url).then(() => {
      sonner.success({
        title: "Link copied to clipboard!",
        description: "You can now share the link with others.",
      })
    })
  }, [template.id])

  useEffect(() => {
    if (initiallyOpen) {
      handleOpen()
    }
  }, [initiallyOpen, handleOpen])

  useEffect(() => {
    if (syncWithUrl && isPublic) {
      setIsOpen(templateId === template.id)
    }
  }, [templateId, template.id, isPublic, syncWithUrl])

  if (isUnlisted && isPublic) {
    return null
  }

  const dialogContent = (
    <DialogContent className="sm:max-w-md w-fit">
      <div className="flex flex-col items-center gap-5 w-fit">
        <h2 className="text-center">{template.name}</h2>

        <div className="flex flex-col w-fit items-center justify-center gap-3">
          {(isUnlisted ? template : paywall) && (
            <PaywallPreviewContainer
              paywall={isUnlisted ? (template as any) : paywall}
              isPublic={isPublic}
            />
          )}
          <span className="text-sm text-muted-foreground tracking-loose">
            Last updated {moment(template.updatedAt).fromNow()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {!isPublic && (
            <Button
              size="icon_lg"
              variant="secondary"
              onClick={isAdmin ? handleEditTemplate : handleShareTemplate}
              title={isAdmin ? "Edit Template" : "Share Template"}
            >
              <SIcon
                type={isAdmin ? "pencil" : "share"}
                className={isAdmin ? "size-5" : "size-[22px]"}
              />
            </Button>
          )}
          <Button
            size="lg"
            variant="default"
            onClick={handleUseTemplate}
            disabled={duplicatePaywallMutation.isLoading}
          >
            {duplicatePaywallMutation.isLoading ? "Loading..." : "Use Template →"}
          </Button>
        </div>
      </div>
    </DialogContent>
  )

  if (!children) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {dialogContent}
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild onClick={handleOpen}>
        {children}
      </DialogTrigger>
      {dialogContent}
    </Dialog>
  )
}

export default TemplateModal
